/**
 * Historial desde el principio: lo que se escribe en la base de datos. Las
 * cuentas se crean con el saldo inicial en el pasado, la historia se marca
 * como revisada (no va a «Por hacer») y cada mes de antes de Rumbo se
 * cuadra (presupuestado = gastado).
 */
import type { send as enviar } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import { integerToAmount } from '@actual-app/core/shared/util';
import type {
  CategoryEntity,
  CategoryGroupEntity,
  ImportTransactionEntity,
} from '@actual-app/core/types/models';
import type { TFunction } from 'i18next';

import { colocarMismoComercio } from '#components/mobile/comercios/buscarMismoComercio';
import type { GrupoComercio } from '#components/mobile/comercios/mismoComercioLogica';
import { nombreCofre } from '#components/mobile/travesia/catalogo';
import type { IdCofre } from '#components/mobile/travesia/catalogo';
import { aqlQuery } from '#queries/aqlQuery';

import {
  agruparComercios,
  cuadreHistoria,
  cuadreMesActual,
  fechaApertura,
  leerHistoria,
  mediasPorCategoria,
  saldoApertura,
} from './historia';
import type { DatosHistoria, MovimientoHistoria } from './historia';

type Enviar = typeof enviar;

/** Primer día del mes `YYYY-MM`. */
function primerDia(mes: string) {
  return `${mes}-01`;
}

/** `202610` → `2026-10`. */
function mesDeEntero(m: number): string {
  const s = String(m);
  return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
}

/** Categorías de gasto (no ingresos) del presupuesto. */
export function categoriasGasto(lista: readonly CategoryEntity[]): Set<string> {
  return new Set(lista.filter(c => !c.is_income).map(c => c.id));
}

/**
 * Categorías ocultas donde va la historia sin ordenar: la de gasto (en su
 * grupo oculto «Historia») y la de entradas (en el grupo de ingresos). Si
 * ya existen (por id o por nombre), se reutilizan.
 */
export async function asegurarCategoriasHistoria(
  send: Enviar,
  t: TFunction,
  previa: DatosHistoria | null,
): Promise<{ categoria: string; entradas?: string }> {
  const { grouped } = (await send('get-categories')) as {
    grouped: CategoryGroupEntity[];
  };
  const todas = grouped.flatMap(g => g.categories ?? []);
  const existe = (id: string | undefined) =>
    id && todas.some(c => c.id === id) ? id : undefined;

  let categoria = existe(previa?.categoria);
  if (!categoria) {
    const nombreGrupo = t('History');
    const nombre = t('Uncategorised (history)');
    const grupo = grouped.find(g => !g.is_income && g.name === nombreGrupo);
    categoria = grupo?.categories?.find(c => c.name === nombre)?.id;
    if (!categoria) {
      const grupoId =
        grupo?.id ??
        (await send('category-group-create', {
          name: nombreGrupo,
          hidden: true,
        }));
      categoria = await send('category-create', {
        name: nombre,
        groupId: grupoId,
        isIncome: false,
        hidden: true,
      });
    }
  }

  let entradas = existe(previa?.entradas);
  const ingresos = grouped.find(g => g.is_income);
  if (!entradas && ingresos) {
    const nombre = t('Money in (history)');
    entradas =
      ingresos.categories?.find(c => c.name === nombre)?.id ??
      (await send('category-create', {
        name: nombre,
        groupId: ingresos.id,
        isIncome: true,
        hidden: true,
      }));
  }
  return entradas ? { categoria, entradas } : { categoria };
}

/**
 * Crea una cuenta con historia: el saldo inicial va el día antes del primer
 * movimiento (lo que tenía entonces) y los movimientos entran sin repetir.
 * Devuelve el id de la cuenta.
 */
export async function crearCuentaConHistoria(
  send: Enviar,
  nombre: string,
  saldoHoy: IntegerAmount,
  movimientos: readonly ImportTransactionEntity[],
): Promise<string> {
  const apertura = saldoApertura(
    saldoHoy,
    movimientos.map(m => m.amount ?? 0),
  );
  const fecha = fechaApertura(movimientos.map(m => m.date));
  const id: string = await send('account-create', {
    name: nombre.trim(),
    balance: integerToAmount(apertura),
    offBudget: false,
  });
  if (fecha) {
    await moverSaldoInicial(send, id, fecha, apertura);
  }
  if (movimientos.length > 0) {
    await send('transactions-import', {
      accountId: id,
      transactions: movimientos.map(m => ({ ...m, account: id })),
      isPreview: false,
    });
  }
  return id;
}

/**
 * «Traer la historia» en una cuenta que ya se tenía: el saldo inicial se
 * recalcula hacia atrás (lo que había antes del primer movimiento de la
 * historia), entran los movimientos, y la historia se marca y se cuadra.
 * El saldo de hoy no cambia. Devuelve la pref `historia` para guardarla.
 */
export async function aplicarHistoriaCuenta(
  send: Enviar,
  t: TFunction,
  {
    cuenta,
    inicio,
    historiaRaw,
    movimientos,
  }: {
    cuenta: string;
    /** Primer mes con Rumbo (`YYYY-MM`). */
    inicio: string;
    historiaRaw: string | null | undefined;
    movimientos: readonly ImportTransactionEntity[];
  },
): Promise<DatosHistoria> {
  const deHistoria = movimientos.filter(m => m.date < primerDia(inicio));
  const fecha = fechaApertura(deHistoria.map(m => m.date));
  if (fecha) {
    const { data } = await aqlQuery(
      q('transactions')
        .filter({ account: cuenta, starting_balance_flag: true })
        .select(['amount'])
        .limit(1),
    );
    const saldoInicial: IntegerAmount = data?.[0]?.amount ?? 0;
    // El saldo inicial ya incluía la historia: se le quita.
    await moverSaldoInicial(
      send,
      cuenta,
      fecha,
      saldoApertura(
        saldoInicial,
        deHistoria.map(m => m.amount ?? 0),
      ),
    );
  }
  await send('transactions-import', {
    accountId: cuenta,
    transactions: movimientos.map(m => ({ ...m, account: cuenta })),
    isPreview: false,
    opts: { matchOnlySameBankLine: true },
  });
  const previa = leerHistoria(historiaRaw);
  const historia: DatosHistoria = {
    inicio: previa?.inicio ?? inicio,
    ...(await asegurarCategoriasHistoria(send, t, previa)),
  };
  await marcarHistoria(send, historia);
  const { list } = await send('get-categories');
  await cuadrar(send, historia.inicio, categoriasGasto(list));
  return historia;
}

/**
 * Pone el saldo inicial de una cuenta en otra fecha y con otro importe (o
 * lo crea si la cuenta empezó en 0).
 */
export async function moverSaldoInicial(
  send: Enviar,
  cuenta: string,
  fecha: string,
  importe: IntegerAmount,
) {
  const { data } = await aqlQuery(
    q('transactions')
      .filter({ account: cuenta, starting_balance_flag: true })
      .select(['id'])
      .limit(1),
  );
  const previo: { id: string } | undefined = data?.[0];
  if (previo) {
    await send('transactions-batch-update', {
      updated: [{ id: previo.id, date: fecha, amount: importe }],
    });
    return;
  }
  if (importe === 0) {
    return;
  }
  const { data: pagadores } = await aqlQuery(
    q('payees').filter({ name: 'Starting Balance' }).select(['id']).limit(1),
  );
  // Como `getStartingBalancePayee`: «Starting Balances» o el primer ingreso.
  const { data: categorias } = await aqlQuery(
    q('categories')
      .filter({ is_income: true })
      .select(['id', 'name'])
      .orderBy({ sort_order: 'asc' }),
  );
  const ingresos = (categorias ?? []) as { id: string; name: string }[];
  const categoria =
    ingresos.find(c => c.name.toLowerCase() === 'starting balances') ??
    ingresos[0];
  await send('transactions-batch-update', {
    added: [
      {
        id: `${cuenta}-apertura`,
        account: cuenta,
        date: fecha,
        amount: importe,
        payee: pagadores?.[0]?.id ?? null,
        category: categoria?.id ?? null,
        cleared: true,
        starting_balance_flag: true,
      },
    ],
  });
}

/** Movimientos de las cuentas del presupuesto desde `desde` (incluido). */
async function movimientosDesde(
  hasta: string | null,
  desde: string | null,
): Promise<MovimientoHistoria[]> {
  let consulta = q('transactions').filter({ 'account.offbudget': false });
  if (hasta) {
    consulta = consulta.filter({ date: { $lt: hasta } });
  }
  if (desde) {
    consulta = consulta.filter({ date: { $gte: desde } });
  }
  const { data } = await aqlQuery(
    consulta.select(['date', 'category', 'amount']),
  );
  return (data ?? []).map(
    (m: { date: string; category: string | null; amount: number }) => ({
      fecha: m.date,
      categoria: m.category,
      importe: m.amount,
    }),
  );
}

/**
 * Marca la historia: lo de antes del primer mes con Rumbo queda revisado
 * (fuera de «Por hacer») y lo que no tiene categoría va a la oculta de la
 * historia (si no, no contaría en el presupuesto y lo por repartir no sería
 * el saldo). Los traspasos entre cuentas se quedan como están.
 */
export async function marcarHistoria(send: Enviar, historia: DatosHistoria) {
  const { data } = await aqlQuery(
    q('transactions')
      .filter({
        'account.offbudget': false,
        date: { $lt: primerDia(historia.inicio) },
        $or: [{ reviewed: false }, { category: null }],
      })
      .select([
        'id',
        'category',
        'amount',
        'transfer_id',
        'starting_balance_flag',
      ]),
  );
  const updated = (data ?? []).map(
    (m: {
      id: string;
      category: string | null;
      amount: number;
      transfer_id: string | null;
      starting_balance_flag: boolean;
    }) => {
      const sinOrdenar =
        m.category == null && m.transfer_id == null && !m.starting_balance_flag;
      // Lo que entró, a «Entradas (historia)»; lo que salió, a la de gasto.
      const destino =
        m.amount > 0 && historia.entradas
          ? historia.entradas
          : historia.categoria;
      return {
        id: m.id,
        reviewed: true,
        ...(sinOrdenar && destino ? { category: destino } : null),
      };
    },
  );
  if (updated.length > 0) {
    await send('transactions-batch-update', { updated });
  }
}

/**
 * Cuadra cada mes de historia: lo presupuestado de cada categoría de gasto
 * es lo gastado, así los sobres acaban en 0. Solo escribe lo que cambia.
 */
export async function cuadrar(
  send: Enviar,
  inicio: string,
  gasto: ReadonlySet<string>,
) {
  const movimientos = await movimientosDesde(primerDia(inicio), null);
  const { data } = await aqlQuery(
    q('zero_budgets')
      .filter({ month: { $lt: Number(inicio.replace('-', '')) } })
      .select(['month', 'category', 'amount']),
  );
  const previo = new Map<string, Map<string, IntegerAmount>>();
  for (const b of (data ?? []) as {
    month: number;
    category: string;
    amount: number;
  }[]) {
    if (!gasto.has(b.category) || !b.amount) {
      continue;
    }
    const mes = mesDeEntero(b.month);
    const cats = previo.get(mes) ?? new Map<string, IntegerAmount>();
    cats.set(b.category, b.amount);
    previo.set(mes, cats);
  }
  const cambios = cuadreHistoria(movimientos, gasto, inicio, previo);
  for (const [mes, amounts] of cambios) {
    await send('budget/budget-amounts', { month: mes, amounts });
  }
}

/**
 * Mes en curso al empezar con historia (travesía): lo ya gastado en cada
 * categoría queda presupuestado, para que lo por repartir sea el saldo de
 * hoy.
 */
export async function cuadrarMesActual(
  send: Enviar,
  mes: string,
  gasto: ReadonlySet<string>,
) {
  const movimientos = await movimientosDesde(
    primerDia(monthUtils.addMonths(mes, 1)),
    primerDia(mes),
  );
  const amounts = cuadreMesActual(movimientos, gasto, mes);
  if (amounts.length > 0) {
    await send('budget/budget-amounts', { month: mes, amounts });
  }
}

/**
 * Lo que se suele gastar en cada categoría según la historia (los 12 meses
 * antes de `inicio`). Vacío si no hay historia.
 */
export async function mediasHistoria(
  inicio: string,
  gasto: ReadonlySet<string>,
): Promise<Map<string, IntegerAmount>> {
  const movimientos = await movimientosDesde(
    primerDia(inicio),
    primerDia(monthUtils.subMonths(inicio, 12)),
  );
  return mediasPorCategoria(movimientos, gasto, inicio);
}

/**
 * Primer mes con Rumbo de un presupuesto que aún no lo tiene apuntado: el
 * del primer movimiento de sus cuentas (o el mes en curso si no hay).
 */
export async function primerMesConRumbo(): Promise<string> {
  const { data } = await aqlQuery(
    q('transactions')
      .filter({ 'account.offbudget': false })
      .select(['date'])
      .orderBy({ date: 'asc' })
      .limit(1),
  );
  const fecha: string | undefined = data?.[0]?.date;
  return fecha ? monthUtils.monthFromDate(fecha) : monthUtils.currentMonth();
}

/**
 * Lo de este mes que llega sin categoría con el extracto (travesía): lo que
 * salió va a la categoría oculta de la historia y lo que entró, a la de
 * entradas, sin aprobar (sigue en «Por revisar»). Así cuenta como cualquier
 * otro gasto o ingreso y lo por repartir es el saldo de hoy.
 */
export async function marcarMesActual(
  send: Enviar,
  historia: DatosHistoria,
  mes: string,
) {
  if (!historia.categoria) {
    return;
  }
  const { data } = await aqlQuery(
    q('transactions')
      .filter({
        'account.offbudget': false,
        category: null,
        is_parent: false,
        transfer_id: null,
        starting_balance_flag: false,
        date: { $gte: primerDia(mes) },
      })
      .select(['id', 'amount']),
  );
  const updated = (data ?? []).map((m: { id: string; amount: number }) => ({
    id: m.id,
    category:
      m.amount > 0 && historia.entradas
        ? historia.entradas
        : historia.categoria,
  }));
  if (updated.length > 0) {
    await send('transactions-batch-update', { updated });
  }
}

/** Adónde va (o vuelve) un movimiento sin ordenar según su signo. */
function ocultaDe(historia: DatosHistoria, importe: IntegerAmount) {
  return importe > 0 && historia.entradas
    ? historia.entradas
    : (historia.categoria ?? '');
}

/** Un comercio de lo sin ordenar con la categoría que propone Gavi. */
export type GrupoHistoria = GrupoComercio & {
  nombre: string;
  detalle: { id: string; importe: IntegerAmount; mes: string }[];
};

/** Cuántos comercios ofrece Gavi para ordenar de una vez. */
const MAXIMO_COMERCIOS = 8;

/**
 * Comercios de lo que está sin ordenar (la historia y lo de este mes en las
 * categorías ocultas) que Gavi reconoce: Mercadona → Supermercado, la
 * nómina → Ingresos… Solo los que tienen categoría a la que ir.
 */
export async function gruposPorComercio(
  send: Enviar,
  t: TFunction,
  historia: DatosHistoria,
  cofres: Partial<Record<string, string>> = {},
  cuenta?: string,
): Promise<GrupoHistoria[]> {
  const ocultas = [historia.categoria, historia.entradas].filter(
    (id): id is string => id != null,
  );
  if (ocultas.length === 0) {
    return [];
  }
  const { data } = await aqlQuery(
    q('transactions')
      .filter({
        'account.offbudget': false,
        category: { $oneof: ocultas },
        is_parent: false,
        transfer_id: null,
        ...(cuenta ? { account: cuenta } : null),
      })
      .select(['id', 'imported_payee', 'amount', 'date']),
  );
  const filas = (data ?? []) as {
    id: string;
    imported_payee: string | null;
    amount: number;
    date: string;
  }[];
  const porId = new Map(filas.map(f => [f.id, f]));
  const { grouped } = (await send('get-categories')) as {
    grouped: CategoryGroupEntity[];
  };
  const visibles = grouped
    .filter(g => !g.hidden)
    .flatMap(g =>
      (g.categories ?? [])
        .filter(c => !c.hidden)
        .map(c => ({ ...c, ingreso: Boolean(g.is_income) })),
    );
  const destino = (sugerencia: IdCofre | 'ingresos') => {
    if (sugerencia === 'ingresos') {
      return visibles.find(c => c.ingreso)?.id;
    }
    const id = cofres[sugerencia];
    if (id && visibles.some(c => c.id === id)) {
      return id;
    }
    const nombre = nombreCofre(t, sugerencia).toLowerCase();
    return visibles.find(
      c => !c.ingreso && c.name.toLowerCase().includes(nombre),
    )?.id;
  };
  const res: GrupoHistoria[] = [];
  const grupos = agruparComercios(
    filas.map(f => ({
      id: f.id,
      concepto: f.imported_payee ?? '',
      importe: f.amount,
    })),
  );
  for (const g of grupos) {
    const categoria = g.sugerencia ? destino(g.sugerencia) : undefined;
    if (!categoria) {
      continue;
    }
    res.push({
      payee: g.clave,
      nombre: g.nombre,
      categoria,
      ids: g.ids,
      detalle: g.ids.flatMap(id => {
        const f = porId.get(id);
        return f
          ? [{ id, importe: f.amount, mes: monthUtils.monthFromDate(f.date) }]
          : [];
      }),
    });
    if (res.length >= MAXIMO_COMERCIOS) {
      break;
    }
  }
  return res;
}

/**
 * Desde el primer mes con Rumbo, lo presupuestado acompaña al movimiento
 * que cambia de categoría (de la oculta a la nueva o al revés), así lo por
 * repartir no se mueve y la categoría nueva no queda en rojo.
 */
async function moverPresupuesto(
  send: Enviar,
  historia: DatosHistoria,
  grupos: readonly GrupoHistoria[],
  gasto: ReadonlySet<string>,
  signo: 1 | -1,
) {
  const porMes = new Map<string, Map<string, IntegerAmount>>();
  const sumar = (mes: string, cat: string, x: IntegerAmount) => {
    if (!gasto.has(cat) || x === 0) {
      return;
    }
    const cats = porMes.get(mes) ?? new Map<string, IntegerAmount>();
    cats.set(cat, (cats.get(cat) ?? 0) + x);
    porMes.set(mes, cats);
  };
  for (const g of grupos) {
    for (const d of g.detalle) {
      if (d.mes < historia.inicio) {
        continue;
      }
      sumar(d.mes, g.categoria, 0 - d.importe * signo);
      sumar(d.mes, ocultaDe(historia, d.importe), d.importe * signo);
    }
  }
  for (const [mes, cats] of porMes) {
    const { data } = await aqlQuery(
      q('zero_budgets')
        .filter({
          month: Number(mes.replace('-', '')),
          category: { $oneof: [...cats.keys()] },
        })
        .select(['category', 'amount']),
    );
    const previo = new Map(
      ((data ?? []) as { category: string; amount: number }[]).map(b => [
        b.category,
        b.amount,
      ]),
    );
    await send('budget/budget-amounts', {
      month: mes,
      amounts: [...cats].map(([category, delta]) => ({
        category,
        amount: (previo.get(category) ?? 0) + delta,
      })),
    });
  }
}

async function gastoActual(send: Enviar) {
  const { list } = (await send('get-categories')) as {
    list: CategoryEntity[];
  };
  return categoriasGasto(list);
}

/** «Sí, ponlos»: los comercios a su categoría y todo cuadrado. */
export async function ordenarHistoria(
  send: Enviar,
  historia: DatosHistoria,
  grupos: readonly GrupoHistoria[],
): Promise<number> {
  const n = await colocarMismoComercio([...grupos]);
  const gasto = await gastoActual(send);
  await moverPresupuesto(send, historia, grupos, gasto, 1);
  await cuadrar(send, historia.inicio, gasto);
  return n;
}

/** «Deshacer»: los comercios vuelven a las ocultas y se cuadra. */
export async function desordenarHistoria(
  send: Enviar,
  historia: DatosHistoria,
  grupos: readonly GrupoHistoria[],
) {
  const updated = grupos.flatMap(g =>
    g.detalle.map(d => ({ id: d.id, category: ocultaDe(historia, d.importe) })),
  );
  if (updated.length > 0) {
    await send('transactions-batch-update', { updated });
  }
  const gasto = await gastoActual(send);
  await moverPresupuesto(send, historia, grupos, gasto, -1);
  await cuadrar(send, historia.inicio, gasto);
}
